import { useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import type { PilabArea } from '@cortex/core-types';
import {
  useCreatePilabRequest,
  useUpdatePilabRequest,
  useUploadPilabAttachment,
} from '../../../hooks/usePilabRequests.js';
import { usePageAccessContext } from '../../../hooks/usePageAccess.js';
import { buildAreaOptions } from './area-options.js';
import type {
  ImprovementRequest,
  ImprovementRequestInput,
  ImprovementRequestSource,
} from '../../../lib/api/pilab-requests.js';

/**
 * 自動入力の初期値。チーム MTG の投稿から流すときに、投稿の中身を入れた状態で開く。
 * @graph-connects none
 */
type ImprovementFormInitial = Partial<Omit<ImprovementRequestInput, 'source'>> & {
  /** 登録する要望に付ける起点（フォームには出さない） */
  source?: ImprovementRequestSource;
};

/**
 * 選んだ画像を base64 にする（`data:...;base64,` の頭は落とす）。
 * @graph-connects none
 */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.replace(/^data:[^,]*,/, ''));
    };
    reader.onerror = () => {
      reject(new Error(reader.error?.message ?? 'read failed'));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * 改善要望を書くフォーム。**登録と書き直しで同じものを使う。**
 *
 * **分類は選ばせない。** 出す人に「これは直すべきか／却下されるか」を判断させると、
 * 却下されそうなものが出てこなくなる。振り分けは AI が基準を読んでやる。
 *
 * 上限や必須条件はここに書かない。弾かれたら API が返した文言をそのまま出す
 * （入口に写すと、API 側で変えたときに黙ってずれる）。
 *
 * `editing` を渡すと書き直しになる。**書き直すと AI の振り分けがやり直しになる**ので、
 * その旨をフォームに出す——黙って振り分けが消えると、判断待ちから外れた理由が分からない。
 *
 * `initial` は**新規登録の初期値**で、`editing`（既存の要望の書き直し）とは別物。入れた値は
 * どれも出す人が直してから登録する前提で、そのまま送らせるための口ではない。`source` は
 * 欄に出さず、登録する要望にだけ付ける。両方渡したときは `editing` を優先する（書き直しで
 * 起点は変えられないため）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 改善要望の入力フォーム。対象画面・要約・内容・理由を受け取って登録または書き直す
 * @graph-connects useCreatePilabRequest [calls] 要望の登録
 * @graph-connects useUpdatePilabRequest [calls] 要望の書き直し
 * @graph-connects buildAreaOptions [calls] 対象画面の選択肢（見られる画面だけ）
 * @graph-connects usePageAccessContext [calls] 見られる画面の判定文脈
 */
export function ImprovementForm({
  editing,
  initial,
  onDone,
}: {
  editing?: ImprovementRequest;
  initial?: ImprovementFormInitial;
  /** 保存できたときに呼ぶ。保存した要望を渡す（起点への記録に ID を使うため） */
  onDone?: (saved: ImprovementRequest) => void;
}): ReactNode {
  const { t } = useTranslation();
  const create = useCreatePilabRequest();
  const update = useUpdatePilabRequest();
  const areaOptions = buildAreaOptions(t, usePageAccessContext());
  const start = editing ?? initial;
  const [area, setArea] = useState<PilabArea>(start?.area ?? 'whole');
  const [title, setTitle] = useState(start?.title ?? '');
  const [body, setBody] = useState(start?.body ?? '');
  const [reason, setReason] = useState(start?.reason ?? '');
  const [attachments, setAttachments] = useState<string[]>(start?.attachments ?? []);
  // 起点は 1 回の登録にだけ付ける。登録後に欄を空にしたら外す（次の要望に付け回さない）
  const [source, setSource] = useState(editing === undefined ? initial?.source : undefined);
  const upload = useUploadPilabAttachment();
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const attach = async (files: FileList | null): Promise<void> => {
    // 失敗の文言は upload.error に残り、下で API が返した文言をそのまま出す。
    // 成功した分だけを選んだ順に追加する
    const results = await Promise.allSettled(
      Array.from(files ?? [])
        .filter((file) => file.type.startsWith('image/'))
        .map(async (file) => {
          const { url } = await upload.mutateAsync({
            contentType: file.type,
            dataBase64: await readAsBase64(file),
          });
          return url;
        }),
    );
    const urls = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
    if (urls.length > 0) setAttachments((current) => [...current, ...urls]);
  };

  const mutation = editing === undefined ? create : update;

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    const input = { area, title, body, reason, attachments };
    const done = (saved: ImprovementRequest): void => {
      if (editing === undefined) {
        setTitle('');
        setBody('');
        setReason('');
        setAttachments([]);
        setSource(undefined);
      }
      onDone?.(saved);
    };
    if (editing === undefined) {
      create.mutate(source === undefined ? input : { ...input, source }, { onSuccess: done });
    } else update.mutate({ id: editing.id, input }, { onSuccess: done });
  };

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div>
        <label className="text-xs text-[var(--color-text-muted)]" htmlFor="improvement-form-area">
          {t('improvements.form.area')}
        </label>
        <Select
          id="improvement-form-area"
          options={areaOptions}
          value={area}
          onChange={(e) => {
            setArea(e.target.value as PilabArea);
          }}
          fullWidth
          size="sm"
        />
      </div>

      <Input
        label={t('improvements.form.title')}
        placeholder={t('improvements.form.titlePlaceholder')}
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
        }}
        fullWidth
        size="sm"
      />

      <div>
        <label className="text-xs text-[var(--color-text-muted)]" htmlFor="improvement-form-body">
          {t('improvements.form.body')}
        </label>
        <Textarea
          id="improvement-form-body"
          rows={3}
          placeholder={t('improvements.form.bodyPlaceholder')}
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
          }}
        />
      </div>

      <div>
        <label className="text-xs text-[var(--color-text-muted)]" htmlFor="improvement-form-reason">
          {t('improvements.form.reason')}
        </label>
        <Textarea
          id="improvement-form-reason"
          rows={3}
          placeholder={t('improvements.form.reasonPlaceholder')}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          {t('improvements.form.reasonHint')}
        </p>
      </div>

      <div>
        <label
          className="text-xs text-[var(--color-text-muted)]"
          htmlFor="improvement-form-attachments"
        >
          {t('improvements.form.attachments')}
        </label>
        <div
          data-testid="improvement-form-dropzone"
          className={`mt-1 flex flex-col items-center gap-2 rounded border border-dashed p-3 ${
            dragging ? 'border-[var(--color-border-focus)] bg-[var(--color-bg-hover)]' : ''
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => {
            setDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void attach(e.dataTransfer.files);
          }}
        >
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => fileInput.current?.click()}
          >
            {t('improvements.form.attachPick')}
          </Button>
          <span className="text-xs text-[var(--color-text-muted)]">
            {t('improvements.form.attachDropHint')}
          </span>
          <input
            id="improvement-form-attachments"
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            hidden
            data-testid="improvement-form-file-input"
            onChange={(e) => {
              void attach(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
        {upload.isPending && (
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {t('improvements.form.uploading')}
          </p>
        )}
        {upload.isError && (
          <p className="mt-1 text-xs text-[var(--color-error)]">{upload.error.message}</p>
        )}
        {attachments.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-2">
            {attachments.map((url) => (
              <li key={url} className="relative">
                <img src={url} alt="" className="h-16 w-16 rounded border object-cover" />
                <button
                  type="button"
                  aria-label={t('improvements.form.removeAttachment')}
                  className="absolute -right-1 -top-1 rounded-full bg-[var(--color-surface)] px-1 text-xs"
                  onClick={() => {
                    setAttachments((current) => current.filter((u) => u !== url));
                  }}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing !== undefined && (
        <p className="text-xs text-[var(--color-warning)]">
          {t('improvements.form.editResetsHint')}
        </p>
      )}

      {mutation.isError && (
        <p className="text-sm text-[var(--color-error)]">{mutation.error.message}</p>
      )}

      <Button type="submit" size="sm" loading={mutation.isPending}>
        {mutation.isPending
          ? t('improvements.form.submitting')
          : editing === undefined
            ? t('improvements.form.submit')
            : t('improvements.form.saveEdit')}
      </Button>
    </form>
  );
}
